import React, { useState, useEffect } from "react";
import { AddCircle, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { useLoadResourceOptions } from '../../hooks/resourcehooks';
import { FoodBusinessExpertCategories, FoodBusinessFundingCategories, FoodBusinessFinancialIntelligenceCategories, usStates, USStatesEnum } from "../../utils/enums";
import { FundingKind, InventorySchema, PartnerHandoffSchema, ResourceSchema, ResourceType, TrainingInventorySchema } from '../../data/types';
import { AddImageBox } from '../../components/AddImageBox';
import { ReactComponent as RightArrow } from "../../icons/ScrollRightArrow.svg";
import { ReactComponent as LeftArrow } from "../../icons/ScrollLeftArrow.svg";
import { AddProductForm } from './AddProductForm';
import { capitalizeString } from '../../utils/helpers';
import { useForm } from "react-hook-form";
import moment from 'moment-timezone';
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AddFarmSchema, AddTrainingResourceSchema, AddFundingStep1Schema, AddFundingResourceSchema, AddFinancialIntelligenceResourceSchema, AddDocumentationResourceSchema, AddExpertResourceSchema, AddKitchenSchema } from '../../utils/validationSchemas';
import {
  AIRIWHEELS_CAPABILITIES,
  AIRIWHEELS_ELIGIBILITY_NOTES,
  AIRIWHEELS_FUNDING_TEMPLATE,
  AIRIWHEELS_PARTNER_SLUG,
  DEFAULT_ATTRIBUTION_WINDOW_DAYS,
  FINANCIAL_WHEEL_OPTIONS,
  FULFILLMENT_MODE_OPTIONS,
  defaultFundingProfile,
  getAiriWheelsSignupUrlFromEnv,
  getAiriWheelsWebhookUrlFromEnv,
} from '../../utils/fundingPartners';
import { FinancialWheelOption } from '../../components/FinancialWheelOption';
import { ReferralCodeLegend } from '../../components/ReferralCodeLegend';
import { FieldLabelWithTooltip } from '../../components/FieldLabelWithTooltip';
import {
  DSCVRY_DESCRIPTION,
  getDscvryHubUrlFromEnv,
  getDscvrySignupUrlFromEnv,
  getDscvryWebhookUrlFromEnv,
} from '../../utils/contactBuilder';

/** Shared partner-handoff seed for non-funding create forms (integration structure only). */
const buildPartnerHandoffTemplate = (): PartnerHandoffSchema => ({
  fulfillment_mode: 'api_handoff',
  partner: {
    signup_url: getAiriWheelsSignupUrlFromEnv() || '',
    webhook_url: getAiriWheelsWebhookUrlFromEnv() || '',
    capabilities: AIRIWHEELS_CAPABILITIES,
  },
});

type PartnerHandoffCreateProps = {
  enabled: boolean
  onToggle: (enabled: boolean) => void
}

/** Optional Partner setup template toggle for kitchen / training / expert / farm / documentation. */
const PartnerHandoffCreateToggle: React.FC<PartnerHandoffCreateProps> = ({ enabled, onToggle }) => (
  <div className="my-4 partner-section-card p-4" data-testid="partner-handoff-create-toggle">
    <p className="page-kicker mb-2">Partner setup</p>
    <label className="flex items-center gap-2 font-open-sans text-sm text-ink cursor-pointer">
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4 rounded border-[#E4EEF1] text-primary-300 accent-primary-300"
      />
      Use partner handoff template
    </label>
    <p className="mt-2 text-xs text-muted font-open-sans">
      Fills fulfillment mode (API handoff) and signup/webhook fields when env URLs are set. Does not add AiriWheels product copy.
    </p>
  </div>
);

type AddResourceFormProps = {
  resourceType: ResourceType
  handleClose: () => void;
  handleAddResource: (resource: ResourceSchema) => void;
}

const AddResourceForm: React.FC<AddResourceFormProps> = ({resourceType, handleAddResource, handleClose}) => {
  const { getResourceIconData } = useLoadResourceOptions()
  const resourceIconData = getResourceIconData(resourceType)

  return (
    <div className='bg-white flex flex-col justify-start w-11/12 mx-auto h-full'>
      {/* <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span> */}
      {
        (resourceType === "farm") ? (
          <AddFarmForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
        ) : (
          (resourceType === "training") ? (
            <AddTrainingForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
          ) : (
            (resourceType === "funding") ? (
              <AddFundingForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
            ) : (
              (resourceType === "expert") ? (
                <AddExpertForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
              ) : (
                (resourceType === "documentation") ? (
                  <AddDocumentationForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
                ) : (
                  (resourceType === "kitchen") ? (
                    <AddKitchenForm resourceType={resourceType} resourceIconData={resourceIconData} handleAddResource={handleAddResource} />
                  ) : (
                    <></>
                  )
                )
              )
            )
          )
        )
      }
      
    </div>
  )
}


type AddFarmFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}
const AddFarmForm: React.FC<AddFarmFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const [step, setStep] = useState<1 | 2>(1)
  const [isAddingProduct, setIsAddingProduct] = useState<boolean>(false)
  const [currentInventoryList, setCurrentInventoryList] = useState<InventorySchema[]>([])
  const [usePartnerHandoff, setUsePartnerHandoff] = useState(false)
  const resourceCategory = resourceIconData.name

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(AddFarmSchema),
    defaultValues: {
      name: '',
      city: '',
      state: '',
      zipcode: '',
      description: '',
      bgPhoto: undefined,
    }
  });

  const [selectedResourceImage, setSelectedResourceImage] = useState<string>("");

  const onSubmit = (data: any) => {
    handleAddResource({
      ...data,
      ...(usePartnerHandoff ? { partner_handoff: buildPartnerHandoffTemplate() } : {}),
    });
  };

  return (step === 1) ? (
    <form onSubmit={handleSubmit(() => setStep(2))}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to set up your fresh food products.</p>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder={`Enter your ${resourceType} name`}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("name")}
        />
        {errors.name && <span className="text-red-500 text-sm font-open-sans">{errors.name.message as string}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of this resource and what it offers.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...register("description")}
        />
        {errors.description && <span className="text-red-500 text-sm font-open-sans">{errors.description.message as string}</span>}
      </div>
      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("city")}
          />
          {errors.city && <span className="text-red-500 text-sm font-open-sans">{errors.city.message as string}</span>}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {errors.state && <span className="text-red-500 text-sm font-open-sans">{errors.state.message as string}</span>}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("zipcode")}
          />
          {errors.zipcode && <span className="text-red-500 text-sm font-open-sans">{errors.zipcode.message as string}</span>}
        </div>
      </div>
      <div className='my-4'>
        <AddImageBox 
          name="bgPhoto"
          title="Upload Images" 
          description="Upload clear images that showcase your resource. Ensure each image is well-lit, high-quality, and at least 800x600 pixels (JPEG or PNG format)." 
          selectedImage={selectedResourceImage}
          setSelectedImage={setSelectedResourceImage}
          handleChange={e => {
            const files = (e.target as HTMLInputElement).files;
            if (files && files.length > 0) {
              setValue("bgPhoto", files[0]);
            }
          }}
        />
        {errors.bgPhoto && <span className="text-red-500 text-sm font-open-sans">{errors.bgPhoto.message as string}</span>}
      </div>
      <PartnerHandoffCreateToggle enabled={usePartnerHandoff} onToggle={setUsePartnerHandoff} />
      <div className='flex flex-row justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          className='border-0 font-raleway text-white bg-primary-200 p-2 rounded-md w-[90px]'
          type="submit"
        >
            Next
        </button>
      </div>
    </form>
  ) : (
    <>
      {
        (isAddingProduct) ? (
          <AddProductForm 
            resourceType={resourceType} 
            resourceCategory={resourceCategory} 
            setIsAddingProduct={setIsAddingProduct} 
            setCurrentInventoryList={setCurrentInventoryList} 
          />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
            <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to set up your fresh food products.</p>
            <div className='my-4'>
              <p className='font-raleway text-black text-[16px] font-medium'>Add Products</p>
              <div className='text-[#4D4D4D] text-[16px] font-open-sans font-thin'>
                Add your individual products here, including names, prices, and images.
              </div>
            </div>
            <div className='flex my-4'>
              <button className='ml-auto bg-black text-white p-2 rounded-md' type="button" onClick={()=> setIsAddingProduct(true)}>
                <AddCircle className='my-auto mr-2' />
                Add Product
              </button>
            </div>
            {
              (currentInventoryList.length > 0) && currentInventoryList.map((inventory, i) => (
                <div key={i} className='flex flex-row my-4 border-[#E9E9E9] border-[1px] h-[266px] rounded-lg'>
                  <div className='w-1/3 bg-slate-300'>
                  </div>
                  <div className='flex flex-col w-2/3'>
                    <p className='mx-4 mt-4 font-raleway text-[18px] text-black font-semibold'>{inventory.item_name}</p>
                    <p className='font-open-sans text-[16px] text-[#4D4D4D] mx-4'>{inventory.item_description}</p>
                    <div className='flex flex-row justify-between border-t-[1px] border-b-[1px] border-[#E9E9E9] h-[51px] my-4 w-11/12 mx-auto'>
                      <p className='my-auto'>{inventory.total_quantity} units</p>
                      <p className='my-auto'>${inventory.unit_price ? inventory.unit_price : 0} per {inventory.measure_unit}</p>
                    </div>
                  </div>
                </div>
              ))
            }
            <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
              <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
              <button className='border-0 font-raleway text-primary-200 font-medium' type="button" onClick={()=>setStep(1)}>Back</button>
              <button 
                className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md'
                type="submit"
              >
                  Publish Resource
              </button>
            </div>
          </form>
        )
      }
    </>
  )
}






type AddDocumentationFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}

const AddDocumentationForm: React.FC<AddDocumentationFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const [isFree, setIsFree] = useState<boolean>(false)
  const [usePartnerHandoff, setUsePartnerHandoff] = useState(false)
  const resourceCategory = resourceIconData.name

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(AddDocumentationResourceSchema),
    defaultValues: {
      name: '',
      description: '',
      total_amount: 0,
      city: '',
      state: '',
      zipcode: '',
    }
  });

  // Watch the isFree state and update total_amount accordingly
  useEffect(() => {
    if (isFree) {
      setValue("total_amount", 0);
    }
  }, [isFree, setValue]);

  // Initialize total_amount to 0 when component mounts
  useEffect(() => {
    setValue("total_amount", 0);
  }, [setValue]);

  // Handle manual changes to total_amount input
  const handleTotalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (value > 0) {
      setIsFree(false);
    }
  };

  const onSubmit = (data: any) => {
    // Ensure total_amount is 0 if isFree is true
    if (isFree) {
      data.total_amount = 0;
    }
    handleAddResource({
      ...data,
      ...(usePartnerHandoff ? { partner_handoff: buildPartnerHandoffTemplate() } : {}),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your documentation resource.</p>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder={`Enter your ${resourceType} name`}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("name")}
        />
        {errors.name && <span className="text-red-500 text-sm font-open-sans">{errors.name.message as string}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of this resource and what it offers.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...register("description")}
        />
        {errors.description && <span className="text-red-500 text-sm font-open-sans">{errors.description.message as string}</span>}
      </div>
      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("city")}
          />
          {errors.city && <span className="text-red-500 text-sm font-open-sans">{errors.city.message as string}</span>}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {errors.state && <span className="text-red-500 text-sm font-open-sans">{errors.state.message as string}</span>}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("zipcode")}
          />
          {errors.zipcode && <span className="text-red-500 text-sm font-open-sans">{errors.zipcode.message as string}</span>}
        </div>
      </div>
      <div className='flex flex-col w-full flex-start mt-4'>
        {
          (isFree) ? (
            <div 
              className={`flex flex-row  hover:bg-[#E9E9E9] text-[16px] font-open-sans rounded-md h-[59px] cursor-pointer`}
              onClick={() => setIsFree(prev=> !prev)}
            >
                <CheckBox 
                  className="mx-2 my-auto text-primary-400" 
                  style={{height: 20, width: 20}} 
                />
                <p className="my-auto">This documentation is free</p>
            </div>
          ) : (
              <>
                <div className="flex flex-col justify-between">
                  <span className="font-raleway text-black text-[16px] font-medium">
                    Documentation Cost ($)
                  </span>
                  <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] w-full rounded-lg">
                    <input
                      placeholder="0.00"
                      className="border-0 bg-inherit w-full my-1 "
                      type="number"
                      step={0.10}
                      min={0}
                      {...register("total_amount", { valueAsNumber: true })}
                      onChange={handleTotalAmountChange}
                    />
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                      <LeftArrow className=" text-black rounded-full  m-auto" />
                    </div>
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                        <RightArrow className="text-black rounded-full  my-auto" />
                    </div>
                  </label>
                  {errors.total_amount && <span className="text-red-500 text-sm font-open-sans">{errors.total_amount.message as string}</span>}
                </div>
                <div 
                  className={`flex flex-row  text-[16px] font-open-sans rounded-md my-1 h-[59px] cursor-pointer`}
                  onClick={() => setIsFree(prev=> !prev)}
                >
                  <CheckBoxOutlineBlank 
                    className="mx-2 text-[#CFCFCF] my-auto" 
                    style={{height: 20, width: 20}} 
                  />
                  <p className="my-auto">This documentation is free</p>
                </div>
              </>
          )
        }
      </div>
      <PartnerHandoffCreateToggle enabled={usePartnerHandoff} onToggle={setUsePartnerHandoff} />
      <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md'
          type="submit"
        >
            Publish Resource
        </button>
      </div>
    </form>
  )
}





type AddTrainingFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}
const AddTrainingForm: React.FC<AddTrainingFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [isAddingDates, setIsAddingDates] = useState<boolean>(false)
  const [currentInventoryList, setCurrentInventoryList] = useState<(TrainingInventorySchema)[]>([])
  const [isFree, setIsFree] = useState<boolean>(false)
  const [usePartnerHandoff, setUsePartnerHandoff] = useState(false)
  const resourceCategory = resourceIconData.name

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(AddTrainingResourceSchema),
    defaultValues: {
      name: '',
      city: '',
      state: '',
      zipcode: '',
      description: '',
      total_amount: 0,
      bgPhoto: undefined,
    }
  });

  // Watch the isFree state and update total_amount accordingly
  useEffect(() => {
    if (isFree) {
      setValue("total_amount", 0);
    }
  }, [isFree, setValue]);

  // Initialize total_amount to 0 when component mounts
  useEffect(() => {
    setValue("total_amount", 0);
  }, [setValue]);

  // Handle manual changes to total_amount input
  const handleTotalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (value > 0) {
      setIsFree(false);
    }
  };

  const [selectedResourceImage, setSelectedResourceImage] = useState<string>("");

  const onSubmit = (data: ResourceSchema) => {
    // Ensure total_amount is 0 if isFree is true
    if (isFree) {
      data.total_amount = 0;
    }
    
    // console.log("currentInventoryList", currentInventoryList);
    if (currentInventoryList.length > 0) {
      data.resource_inventory = { schedule: currentInventoryList };
      // console.log("add inventory items");
    }

    // console.log("data", data);
    handleAddResource({
      ...data,
      ...(usePartnerHandoff ? { partner_handoff: buildPartnerHandoffTemplate() } : {}),
    });
  };

  return (step === 1) ? (
    <form onSubmit={handleSubmit(() => setStep(2))}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your culinary training resource.</p>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder={`Enter your ${resourceType} name`}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("name")}
        />
        {errors.name && <span className="text-red-500 text-sm font-open-sans">{errors.name.message as string}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of this resource and what it offers.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...register("description")}
        />
        {errors.description && <span className="text-red-500 text-sm font-open-sans">{errors.description.message as string}</span>}
      </div>
      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("city")}
          />
          {errors.city && <span className="text-red-500 text-sm font-open-sans">{errors.city.message as string}</span>}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {errors.state && <span className="text-red-500 text-sm font-open-sans">{errors.state.message as string}</span>}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("zipcode")}
          />
          {errors.zipcode && <span className="text-red-500 text-sm font-open-sans">{errors.zipcode.message as string}</span>}
        </div>
      </div>
      <div className='flex flex-col w-full flex-start mt-4'>
        {
          (isFree) ? (
            <div 
              className={`flex flex-row  hover:bg-[#E9E9E9] text-[16px] font-open-sans rounded-md h-[59px] cursor-pointer`}
              onClick={() => setIsFree(prev=> !prev)}
            >
                <CheckBox 
                  className="mx-2 my-auto text-primary-400" 
                  style={{height: 20, width: 20}} 
                />
                <p className="my-auto">This training is free</p>
            </div>
          ) : (
              <>
                <div className="flex flex-col justify-between">
                  <span className="font-raleway text-black text-[16px] font-medium">
                    Training Cost ($)
                  </span>
                  <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] w-full rounded-lg">
                    <input
                      placeholder="0.00"
                      className="border-0 bg-inherit w-full my-1 "
                      type="number"
                      step={0.10}
                      min={0}
                      {...register("total_amount", { valueAsNumber: true })}
                      onChange={handleTotalAmountChange}
                    />
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                      <LeftArrow className=" text-black rounded-full  m-auto" />
                    </div>
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                        <RightArrow className="text-black rounded-full  my-auto" />
                    </div>
                  </label>
                  {errors.total_amount && <span className="text-red-500 text-sm font-open-sans">{errors.total_amount.message as string}</span>}
                </div>
                <div 
                  className={`flex flex-row  text-[16px] font-open-sans rounded-md my-1 h-[59px] cursor-pointer`}
                  onClick={() => setIsFree(prev=> !prev)}
                >
                  <CheckBoxOutlineBlank 
                    className="mx-2 text-[#CFCFCF] my-auto" 
                    style={{height: 20, width: 20}} 
                  />
                  <p className="my-auto">This training is free</p>
                </div>
              </>
          )
        }
      </div>
      <PartnerHandoffCreateToggle enabled={usePartnerHandoff} onToggle={setUsePartnerHandoff} />
      <div className='flex flex-row justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          className='border-0 font-raleway text-white bg-primary-200 p-2 rounded-md w-[90px]'
          type="submit"
        >
            Next
        </button>
      </div>
    </form>
  ) : (
    (step === 2) ? (
      <form onSubmit={handleSubmit(() => setStep(3))}>
        <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
        <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your culinary training resource.</p>
        <div className='my-4'>
          <AddImageBox 
            name="bgPhoto"
            title="Upload Images" 
            description="Upload clear images that showcase your resource. Ensure each image is well-lit, high-quality, and at least 800x600 pixels (JPEG or PNG format)." 
            selectedImage={selectedResourceImage}
            setSelectedImage={setSelectedResourceImage}
            handleChange={e => {
              const files = (e.target as HTMLInputElement).files;
              if (files && files.length > 0) {
                setValue("bgPhoto", files[0]);
              }
            }}
          />
          {errors.bgPhoto && <span className="text-red-500 text-sm font-open-sans">{errors.bgPhoto.message as string}</span>}
        </div>
        <div className='flex flex-row justify-between w-11/12 mx-auto my-4 border-t-[1px] border-[#E9E9E9] py-2'>
          <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
          <button className='border-0 font-raleway text-primary-200 font-medium' type="button" onClick={()=>setStep(1)}>Back</button>
          <button 
            className='border-0 font-raleway text-white bg-primary-200 p-2 rounded-md w-[90px]'
            type="submit"
          >
              Next
          </button>
        </div>
      </form>
    ) : (
      <>
        {
          (isAddingDates) ? (
            <AddProductForm 
              resourceType={resourceType} 
              resourceCategory={resourceCategory} 
              setIsAddingProduct={setIsAddingDates} 
              setCurrentInventoryList={setCurrentInventoryList} 
            />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
            <>
              <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
              <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your culinary training resource.</p>
              <div className='my-4'>
                <p className='font-raleway text-black text-[16px] font-medium'>Add Training Schedule</p>
                <div className='text-[#4D4D4D] text-[16px] font-open-sans font-thin'>
                  Add your individual training schedule here
                </div>
              </div>
              <div className='flex my-4'>
                <button className='ml-auto bg-black text-white p-2 rounded-md' type="button" onClick={()=> setIsAddingDates(true)}>
                  <AddCircle className='my-auto mr-2' />
                  Add Schedule
                </button>
              </div>
              {
                (currentInventoryList.length > 0) && currentInventoryList.map((inventory, i) => (
                  <div key={i} className='flex flex-row my-4 border-[#E9E9E9] border-[1px] h-[266px] rounded-lg'>
                    <div className='w-1/3 bg-slate-300'>
                    </div>
                    <div className='flex flex-col w-2/3'>
                      <p className='mx-4 mt-4 font-raleway text-[18px] text-black font-semibold'>{inventory.item_name}</p>
                      <p className='font-open-sans text-[16px] text-[#4D4D4D] mx-4'>{inventory.item_description}</p>
                      <div className='flex flex-row justify-between border-t-[1px] border-b-[1px] border-[#E9E9E9] h-[51px] my-4 w-11/12 mx-auto'>
                        <p className='my-auto'>{inventory.total_quantity} seats</p>
                        <p className='my-auto'>{inventory.duration} hours</p>
                        <p className='my-auto'>{inventory.unit_price ? `$${inventory.unit_price}` : 'Free'}</p>
                      </div>
                      <div className='flex flex-row justify-between border-t-[1px] border-b-[1px] border-[#E9E9E9] h-[51px] my-4 w-11/12 mx-auto'>
                        <p className='my-auto'>{inventory.times.map((time: string) => <span className='mr-2 p-1 bg-primary-300 rounded-md text-white my-1' key={time}>{moment(time, 'HH:mm').format('hh:mma')}</span>)}</p>
                        {/* <p className='my-auto'>{inventory.times.map((time: string) => moment(time, 'HH:mm').format('hh:mma')).join(', ')}</p> */}
                      </div>
                    </div>
                  </div>
                ))
              }
              <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
                <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
                <button className='border-0 font-raleway text-primary-200 font-medium' type="button" onClick={()=>setStep(2)}>Back</button>
                <button 
                  className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md'
                  type="submit"
                >
                    Publish Resource
                </button>
              </div>
            </>
            </form>
          )
        }
      </>
    )
  )
}



type AddKitchenFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}

const AddKitchenForm: React.FC<AddKitchenFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const resourceCategory = resourceIconData.name
  const [usePartnerHandoff, setUsePartnerHandoff] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<any>({
    resolver: zodResolver(AddKitchenSchema),
    defaultValues: {
      name: "",
      address: "",
      description: "",
      city: "",
      state: "",
      zipcode: "",
      bgPhoto: undefined,
    }
  });

  const [selectedResourceImage, setSelectedResourceImage] = useState<string>("");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setValue("bgPhoto", files[0]);
    }
  };

  const onSubmit = (data: any) => {
    handleAddResource({
      ...data,
      ...(usePartnerHandoff ? { partner_handoff: buildPartnerHandoffTemplate() } : {}),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill in the details below to add your Kitchen Space.</p>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder={`Enter your ${resourceType} name`}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("name")}
        />
        {errors.name && <span className="text-red-500 text-sm font-open-sans">{String(errors.name?.message)}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Address</p>
        <input 
          type='text' 
          placeholder='Enter street address'
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("address")}
        />
        {errors.address && <span className="text-red-500 text-sm font-open-sans">{String(errors.address?.message)}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of this resource and what it offers.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...register("description")}
        />
        {errors.description && <span className="text-red-500 text-sm font-open-sans">{String(errors.description?.message)}</span>}
      </div>

      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("city")}
          />
          {errors.city && <span className="text-red-500 text-sm font-open-sans">{String(errors.city?.message)}</span>}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {errors.state && <span className="text-red-500 text-sm font-open-sans">{String(errors.state?.message)}</span>}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("zipcode")}
          />
          {errors.zipcode && <span className="text-red-500 text-sm font-open-sans">{String(errors.zipcode?.message)}</span>}
        </div>
      </div>

      <div className='my-4'>
        <AddImageBox 
          name="bgPhoto"
          title="Upload Images" 
          description="Upload clear images that showcase your resource. Ensure each image is well-lit, high-quality, and at least 800x600 pixels (JPEG or PNG format)." 
          selectedImage={selectedResourceImage}
          setSelectedImage={setSelectedResourceImage}
          handleChange={handleFileChange}
        />
        {errors.bgPhoto && <span className="text-red-500 text-sm font-open-sans">{String(errors.bgPhoto?.message)}</span>}
      </div>

      <PartnerHandoffCreateToggle enabled={usePartnerHandoff} onToggle={setUsePartnerHandoff} />

      <div className='flex flex-row justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          data-testid="publish-resource-button"
          className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md'
          type="submit"
        >
            Publish Resource
        </button>
      </div>
    </form>
  )
}





type AddFundingFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}

const fundingFormDefaults = {
  funding_kind: 'capital' as FundingKind,
  name: '',
  city: '',
  state: '',
  zipcode: '',
  description: '',
  resource_subtype: '',
  website: '',
  total_amount: 0,
  end_time: '',
  bgPhoto: undefined as File | undefined,
  fulfillment_mode: 'api_handoff' as const,
  wheels: [] as string[],
  requires_active_sales: true,
  eligibility_notes: '',
  partner_slug: '',
  partner_display_name: '',
  signup_url: '',
  partner_referral_code: '',
  attribution_window_days: DEFAULT_ATTRIBUTION_WINDOW_DAYS,
  privacy_url: '',
  webhook_url: '',
  pricing_summary: '',
  trial_days: 0,
};

const AddFundingForm: React.FC<AddFundingFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const [step, setStep] = useState<1 | 2>(1)
  const resourceCategory = resourceIconData.name
  const [selectedResourceImage, setSelectedResourceImage] = useState<string>("");
  const [isAiriWheelsListing, setIsAiriWheelsListing] = useState(false);

  const step1Form = useForm({
    resolver: zodResolver(AddFundingStep1Schema),
    defaultValues: fundingFormDefaults,
  });

  const fullForm = useForm({
    resolver: zodResolver(AddFundingResourceSchema),
    defaultValues: fundingFormDefaults,
  });

  const fundingKind = step1Form.watch('funding_kind');
  const selectedWheels = fullForm.watch('wheels') || [];

  /** Apply AiriWheels identity + optional env URLs when marking this as the AiriWheels listing. */
  const applyAiriWheelsListingIdentity = () => {
    const template = AIRIWHEELS_FUNDING_TEMPLATE;
    const profile = template.funding_profile;
    const signupFromEnv = getAiriWheelsSignupUrlFromEnv();
    const webhookFromEnv = getAiriWheelsWebhookUrlFromEnv();
    step1Form.setValue('funding_kind', 'financial_intelligence');
    step1Form.setValue('name', template.name || '');
    step1Form.setValue('description', template.description || '');
    step1Form.setValue('resource_subtype', template.resource_subtype || '');
    step1Form.setValue('website', template.website || '');
    step1Form.setValue('city', template.city || '');
    step1Form.setValue('state', template.state || '');
    step1Form.setValue('zipcode', template.zipcode || '');
    step1Form.setValue('wheels', profile?.wheels || []);
    fullForm.setValue('funding_kind', 'financial_intelligence');
    fullForm.setValue('name', template.name || '');
    fullForm.setValue('description', template.description || '');
    fullForm.setValue('resource_subtype', template.resource_subtype || '');
    fullForm.setValue('website', template.website || '');
    fullForm.setValue('city', template.city || '');
    fullForm.setValue('state', template.state || '');
    fullForm.setValue('zipcode', template.zipcode || '');
    fullForm.setValue('fulfillment_mode', 'api_handoff');
    fullForm.setValue('wheels', profile?.wheels || []);
    fullForm.setValue('requires_active_sales', true);
    fullForm.setValue('eligibility_notes', AIRIWHEELS_ELIGIBILITY_NOTES);
    fullForm.setValue('partner_slug', AIRIWHEELS_PARTNER_SLUG);
    fullForm.setValue('partner_display_name', 'AiriWheels');
    fullForm.setValue('signup_url', signupFromEnv || profile?.partner?.signup_url || '');
    fullForm.setValue('webhook_url', webhookFromEnv || profile?.partner?.webhook_url || '');
    fullForm.setValue('attribution_window_days', DEFAULT_ATTRIBUTION_WINDOW_DAYS);
    fullForm.setValue('privacy_url', profile?.partner?.privacy_url || '');
    fullForm.setValue('pricing_summary', profile?.pricing_summary || '');
    fullForm.setValue('trial_days', profile?.trial_days || 0);
  };

  /** Clear AiriWheels-specific identity when switching to a generic FI listing. */
  const clearAiriWheelsListingIdentity = () => {
    step1Form.setValue('wheels', []);
    fullForm.setValue('wheels', []);
    fullForm.setValue('partner_slug', '');
    fullForm.setValue('partner_display_name', '');
    fullForm.setValue('signup_url', '');
    fullForm.setValue('webhook_url', '');
    fullForm.setValue('eligibility_notes', '');
    fullForm.setValue('privacy_url', '');
    fullForm.setValue('pricing_summary', '');
    fullForm.setValue('trial_days', 0);
  };

  /**
   * Use template fills integration structure only (fulfillment + handoff URLs when env set).
   * Does not stamp AiriWheels name/wheels/eligibility onto a non-AiriWheels listing.
   */
  const applyPartnerHandoffTemplate = () => {
    fullForm.setValue('fulfillment_mode', 'api_handoff');
    fullForm.setValue('attribution_window_days', DEFAULT_ATTRIBUTION_WINDOW_DAYS);
    if (isAiriWheelsListing) {
      const signupFromEnv = getAiriWheelsSignupUrlFromEnv();
      const webhookFromEnv = getAiriWheelsWebhookUrlFromEnv();
      if (signupFromEnv) fullForm.setValue('signup_url', signupFromEnv);
      if (webhookFromEnv) fullForm.setValue('webhook_url', webhookFromEnv);
      fullForm.setValue('partner_slug', AIRIWHEELS_PARTNER_SLUG);
      fullForm.setValue('partner_display_name', fullForm.getValues('partner_display_name') || 'AiriWheels');
    }
  };

  const handleAiriWheelsListingToggle = (checked: boolean) => {
    setIsAiriWheelsListing(checked);
    if (checked) {
      applyAiriWheelsListingIdentity();
    } else {
      clearAiriWheelsListingIdentity();
    }
  };

  const handleStep1Submit = (data: any) => {
    Object.keys(data).forEach((key) => {
      fullForm.setValue(key as any, data[key]);
    });
    if (data.funding_kind === 'financial_intelligence' && !fullForm.getValues('fulfillment_mode')) {
      fullForm.setValue('fulfillment_mode', 'api_handoff');
    }
    setStep(2);
  };

  const handleStep2Submit = (data: any) => {
    const kind: FundingKind = data.funding_kind || 'capital';
    const profile = defaultFundingProfile(kind);
    if (kind === 'financial_intelligence') {
      profile.fulfillment_mode = data.fulfillment_mode || 'api_handoff';
      profile.wheels = isAiriWheelsListing ? (data.wheels || []) : [];
      profile.eligibility = {
        requires_active_sales: data.requires_active_sales !== false,
        excluded_stages: data.requires_active_sales !== false ? ['idea'] : [],
        notes: data.eligibility_notes || '',
      };
      profile.partner = {
        slug: isAiriWheelsListing ? (data.partner_slug || AIRIWHEELS_PARTNER_SLUG) : (data.partner_slug || ''),
        display_name: data.partner_display_name || '',
        signup_url: data.signup_url || data.website || '',
        referral_code: data.partner_referral_code || '',
        attribution_window_days: data.attribution_window_days || DEFAULT_ATTRIBUTION_WINDOW_DAYS,
        privacy_url: data.privacy_url || '',
        webhook_url: data.webhook_url || '',
        capabilities: isAiriWheelsListing
          ? AIRIWHEELS_CAPABILITIES
          : ['deep_link', 'outbound_webhook', 'inbound_webhook'],
      };
      profile.pricing_summary = data.pricing_summary || '';
      profile.trial_days = data.trial_days || undefined;
    }
    handleAddResource({
      ...data,
      website: data.website,
      funding_profile: profile,
    });
  };

  const subtypeOptions = fundingKind === 'financial_intelligence'
    ? Object.values(FoodBusinessFinancialIntelligenceCategories)
    : Object.values(FoodBusinessFundingCategories);

  return (step === 1) ? (
    <form onSubmit={step1Form.handleSubmit(handleStep1Submit)}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add a capital listing or a financial-intelligence partner such as AiriWheels.</p>
      <div className="flex flex-col my-4">
        <FieldLabelWithTooltip
          label="Resource kind"
          tooltip="Capital listings collect applications in HomeCook. Financial intelligence listings create a consented partner intro as a funding booking."
        />
        <select
          {...step1Form.register("funding_kind")}
          className="formInput border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          onChange={(e) => {
            const kind = e.target.value as FundingKind;
            step1Form.setValue('funding_kind', kind);
            step1Form.setValue('resource_subtype', '');
            fullForm.setValue('funding_kind', kind);
            fullForm.setValue('resource_subtype', '');
          }}
        >
          <option value="capital">Capital (loan, grant, or financing)</option>
          <option value="financial_intelligence">Financial intelligence</option>
        </select>
      </div>
      {fundingKind === 'financial_intelligence' && (
        <div className="mb-4 space-y-3">
          <label className="flex items-center gap-2 font-open-sans text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              data-testid="airiwheels-listing-checkbox"
              checked={isAiriWheelsListing}
              onChange={(e) => handleAiriWheelsListingToggle(e.target.checked)}
              className="h-4 w-4 rounded border-[#E4EEF1] text-primary-300 accent-primary-300"
            />
            This is the AiriWheels partner listing
          </label>
          <button
            type="button"
            data-testid="airiwheels-template-button"
            className="border-[1px] border-primary-200 text-primary-200 font-raleway rounded-md px-3 py-2"
            onClick={applyPartnerHandoffTemplate}
          >
            Use template
          </button>
          <p className="text-xs text-muted font-open-sans">
            Template fills fulfillment mode and partner handoff URLs when available. It does not copy AiriWheels product details onto a generic listing.
          </p>
        </div>
      )}
      <div className="flex flex-col text-primary-200 my-4">
        <span className="font-raleway text-black text-[16px] font-medium">
          Funding Type
        </span>
        <select
          {...step1Form.register("resource_subtype")}
          className="formInput border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
        >
          <option value="" disabled>Select funding type</option>
          {subtypeOptions.map((resource_subtype, i) => (
            <option key={i} value={resource_subtype}>
              {capitalizeString(resource_subtype)}
            </option>
          ))}
        </select>
        {step1Form.formState.errors.resource_subtype && (
          <span className="text-red-500 text-sm">{step1Form.formState.errors.resource_subtype.message as string}</span>
        )}
      </div>
      {fundingKind === 'financial_intelligence' && isAiriWheelsListing && (
        <div className="my-4">
          <p className='font-raleway text-black text-[16px] font-medium'>Product wheels</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {FINANCIAL_WHEEL_OPTIONS.map((wheel) => (
              <FinancialWheelOption
                key={wheel.value}
                label={wheel.label}
                checked={selectedWheels.includes(wheel.value)}
                onChange={(checked) => {
                  const next = checked
                    ? [...selectedWheels, wheel.value]
                    : selectedWheels.filter((value) => value !== wheel.value);
                  step1Form.setValue('wheels', next);
                  fullForm.setValue('wheels', next);
                }}
              />
            ))}
          </div>
        </div>
      )}
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder={`Enter your ${resourceType} name`}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...step1Form.register("name")}
        />
        {step1Form.formState.errors.name && (
          <span className="text-red-500 text-sm">{step1Form.formState.errors.name.message as string}</span>
        )}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of this resource and what it offers.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...step1Form.register("description")}
        />
        {step1Form.formState.errors.description && (
          <span className="text-red-500 text-sm">{step1Form.formState.errors.description.message as string}</span>
        )}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Website</p>
        <input
          type='url'
          placeholder='https://'
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...step1Form.register("website")}
        />
      </div>
      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...step1Form.register("city")}
          />
          {step1Form.formState.errors.city && (
            <span className="text-red-500 text-sm">{step1Form.formState.errors.city.message as string}</span>
          )}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...step1Form.register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {step1Form.formState.errors.state && (
            <span className="text-red-500 text-sm">{step1Form.formState.errors.state.message as string}</span>
          )}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...step1Form.register("zipcode")}
          />
          {step1Form.formState.errors.zipcode && (
            <span className="text-red-500 text-sm">{step1Form.formState.errors.zipcode.message as string}</span>
          )}
        </div>
      </div>
      <div className='my-4'>
        <AddImageBox 
          name="bgPhoto"
          title="Upload Images" 
          description="Upload clear images that showcase your resource. Ensure each image is well-lit, high-quality, and at least 800x600 pixels (JPEG or PNG format)." 
          selectedImage={selectedResourceImage}
          setSelectedImage={setSelectedResourceImage}
          handleChange={e => {
            const files = (e.target as HTMLInputElement).files;
            if (files && files.length > 0) {
              fullForm.setValue("bgPhoto", files[0]);
            }
          }}
        />
      </div>
      <div className='flex flex-row justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          className='border-0 font-raleway text-white bg-primary-200 p-2 rounded-md w-[90px]'
          type="submit"
        >
            Next
        </button>
      </div>
    </form>
  ) : (
    <form onSubmit={fullForm.handleSubmit(handleStep2Submit)}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your funding resource.</p>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <h4 className="font-raleway text-black text-[16px] font-medium mb-2">Review Information</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Name:</strong> {fullForm.getValues("name")}</div>
          <div><strong>Kind:</strong> {fullForm.getValues("funding_kind") === 'financial_intelligence' ? 'Financial intelligence' : 'Capital'}</div>
          <div><strong>Funding Type:</strong> {fullForm.getValues("resource_subtype")}</div>
          <div><strong>City:</strong> {fullForm.getValues("city")}</div>
          <div><strong>State:</strong> {fullForm.getValues("state")}</div>
          <div><strong>Zipcode:</strong> {fullForm.getValues("zipcode")}</div>
          <div><strong>Description:</strong> {fullForm.getValues("description")?.substring(0, 50)}...</div>
        </div>
      </div>

      {fullForm.watch('funding_kind') === 'financial_intelligence' ? (
        <>
          <div className="partner-section-card p-5 mb-4">
            <p className="page-kicker">Eligibility</p>
            <div className="flex flex-col my-3 w-full">
              <FieldLabelWithTooltip label="Fulfillment mode" tooltip="How members receive this offer: in-product application, partner referral handoff, API handoff, or an external trial." />
              <select {...fullForm.register("fulfillment_mode")} className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]'>
                {FULFILLMENT_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 my-3 font-open-sans text-sm text-ink">
              <input type="checkbox" {...fullForm.register("requires_active_sales")} />
              Requires commercially active sales
            </label>
            <div className="my-3">
              <p className='font-raleway text-ink text-[16px] font-medium'>Eligibility notes</p>
              <textarea className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[80px] text-[16px]' {...fullForm.register("eligibility_notes")} />
            </div>
          </div>
          <div className="partner-section-card p-5 mb-4">
            <p className="page-kicker">Partner</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <p className='font-raleway text-ink text-[16px] font-medium'>Partner slug</p>
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("partner_slug")} />
              </div>
              <div>
                <p className='font-raleway text-ink text-[16px] font-medium'>Partner display name</p>
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("partner_display_name")} />
              </div>
              <div>
                <FieldLabelWithTooltip label="Signup URL" tooltip="Partner destination members open after a consented intro. HomeCook appends the referral code." />
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("signup_url")} />
              </div>
              <div>
                <FieldLabelWithTooltip label="Webhook URL" tooltip="HTTPS endpoint HomeCook calls with signed partner events. Secrets are stored on the server, not in this form." />
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("webhook_url")} />
              </div>
              <div>
                <FieldLabelWithTooltip label="Referral code" tooltip="Optional partner-facing code. Channel prefixes: pantry PAN, joint seminar SEM, incubator INC, owned digital DIG, print PRT." />
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("partner_referral_code")} />
              </div>
              <div>
                <FieldLabelWithTooltip label="Attribution window (days)" tooltip="How long a consented intro stays attributed to this partner after the first touch." />
                <input type="number" min={1} className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("attribution_window_days", { valueAsNumber: true })} />
              </div>
            </div>
            <p className="mt-3 text-xs font-open-sans text-muted">
              Referral format: <ReferralCodeLegend />. Channel prefixes: pantry PAN, joint seminar SEM, incubator INC, owned digital DIG, print PRT.
            </p>
          </div>
          <div className="partner-section-card p-5 mb-4">
            <p className="page-kicker">Pricing</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <FieldLabelWithTooltip label="Pricing summary" tooltip="Public-facing price language shown on the pantry listing, not a charge amount." />
                <input className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("pricing_summary")} />
              </div>
              <div>
                <FieldLabelWithTooltip label="Trial days" tooltip="Optional partner trial length described to members. This does not create a HomeCook subscription." />
                <input type="number" min={0} className='border-[#E4EEF1] border-[1px] w-full rounded-lg h-[50px] text-[16px]' {...fullForm.register("trial_days", { valueAsNumber: true })} />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col my-4 w-full">
            <FieldLabelWithTooltip
              label="Application close date"
              tooltip="Optional. Last day this offer accepts applications. Leave empty to keep the listing open."
            />
            <input 
              type='date' 
              className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
              {...fullForm.register("end_time")}
            />
            {fullForm.formState.errors.end_time && <span className="text-red-500 text-sm">{fullForm.formState.errors.end_time.message as string}</span>}
          </div>
          <div className="flex flex-col justify-between">
            <span className="font-raleway text-black text-[16px] font-medium">
            Funding amount
            </span>
            <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] w-full rounded-lg">
              <input
                placeholder="0.00"
                className="border-0 bg-inherit w-full my-1 "
                type="number"
                step={10}
                min={0}
                {...fullForm.register("total_amount", { valueAsNumber: true })}
              />
              <div
                className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
              >
                <LeftArrow className=" text-black rounded-full  m-auto" />
              </div>
              <div
                className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
              >
                  <RightArrow className="text-black rounded-full  my-auto" />
              </div>
            </label>
            {fullForm.formState.errors.total_amount && <span className="text-red-500 text-sm">{fullForm.formState.errors.total_amount.message as string}</span>}
          </div>
        </>
      )}
      
      <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button className='border-0 font-raleway text-primary-200 font-medium' type="button" onClick={()=>setStep(1)}>Back</button>
        <button 
          data-testid="publish-resource-button"
          className='border-0 font-raleway w-[160px] text-white bg-primaryDark-0 hover:bg-primary-300 h-12 rounded-lg'
          type="submit"
        >
            Publish Resource
        </button>
      </div>  
    </form>
  )
}




type AddExpertFormProps = {
  resourceType: ResourceType
  resourceIconData: any
  handleAddResource: (resource: ResourceSchema) => void;
}
const AddExpertForm: React.FC<AddExpertFormProps> = ({resourceType, resourceIconData, handleAddResource}) => {
  const [isFree, setIsFree] = useState<boolean>(true)
  const [usePartnerHandoff, setUsePartnerHandoff] = useState(false)
  const [isDscvryListing, setIsDscvryListing] = useState(false)
  const resourceCategory = resourceIconData.name
  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(AddExpertResourceSchema),
    defaultValues: {
      name: '',
      city: '',
      state: '',
      zipcode: '',
      description: '',
      resource_subtype: '',
      total_amount: 0,
      bgPhoto: undefined,
    }
  });

  // Watch the isFree state and update total_amount accordingly
  useEffect(() => {
    if (isFree) {
      setValue("total_amount", 0);
    }
  }, [isFree, setValue]);

  // Initialize total_amount to 0 when component mounts since isFree starts as true
  useEffect(() => {
    setValue("total_amount", 0);
  }, [setValue]);

  // Handle manual changes to total_amount input
  const handleTotalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (value > 0) {
      setIsFree(false);
    }
  };

  const [selectedResourceImage, setSelectedResourceImage] = useState<string>("");

  const applyDscvryListing = () => {
    setIsDscvryListing(true);
    setUsePartnerHandoff(true);
    setValue('name', 'DSCVRY Contact List Builder');
    setValue('description', DSCVRY_DESCRIPTION);
    setValue('resource_subtype', FoodBusinessExpertCategories.ContactListBuilder);
    setValue('city', 'Richmond');
    setValue('state', 'VA');
    setValue('zipcode', '23223');
  };

  const onSubmit = (data: any) => {
    // Ensure total_amount is 0 if isFree is true
    if (isFree) {
      data.total_amount = 0;
    }
    const partnerHandoff = isDscvryListing
      ? {
          fulfillment_mode: 'api_handoff' as const,
          partner: {
            slug: 'dscvry',
            display_name: 'DSCVRY',
            signup_url: getDscvrySignupUrlFromEnv(),
            hub_url: getDscvryHubUrlFromEnv(),
            webhook_url: getDscvryWebhookUrlFromEnv(),
            capabilities: ['deep_link', 'outbound_webhook', 'inbound_webhook', 'contact_research'],
          },
        }
      : (usePartnerHandoff ? buildPartnerHandoffTemplate() : undefined);
    handleAddResource({
      ...data,
      ...(isDscvryListing ? { website: 'https://www.dscvry.co/' } : {}),
      ...(partnerHandoff ? { partner_handoff: partnerHandoff } : {}),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Resource</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add industry expertise.</p>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Name</p>
        <input 
          type='text' 
          placeholder='Enter name' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
          {...register("name")}
        />
        {errors.name && <span className="text-red-500 text-sm">{errors.name.message as string}</span>}
      </div>
      <div className="flex flex-col text-primary-200 my-4">
        <span className="font-raleway text-black text-[16px] font-medium">
          Expertise
        </span>
        <select
          {...register("resource_subtype")}
          className="formInput border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
        >
          <option value="" disabled>Select business specializations</option>
          {Object.values(FoodBusinessExpertCategories).map((resource_subtype, i) => (
            <option key={i} value={resource_subtype}>
              {capitalizeString(resource_subtype)}
            </option>
          ))}
          <option value="other">Other</option>
        </select>
        {errors.resource_subtype && <span className="text-red-500 text-sm">{errors.resource_subtype.message as string}</span>}
      </div>
      <div className='my-4'>
        <p className='font-raleway text-black text-[16px] font-medium'>Description</p>
        <textarea 
          placeholder='Provide a brief description of what you offer.' 
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[120px] text-[16px]'
          {...register("description")}
        />
        {errors.description && <span className="text-red-500 text-sm">{errors.description.message as string}</span>}
      </div>
      <div className='flex flex-row'>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>City</p>
          <input 
            type='text' 
            placeholder='Enter City' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("city")}
          />
          {errors.city && <span className="text-red-500 text-sm">{errors.city.message as string}</span>}
        </div>
        <div className='my-4 mr-1'>
          <p className='font-raleway text-black text-[16px] font-medium'>State</p>
          <select 
            {...register("state")}
            className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
          >
            <option value="">-- Select State --</option>
            <option value="nationwide">All</option>
            {
                (usStates && usStates.length > 0) && usStates.map((state, i) => (
                    <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                ))
            }
          </select>
          {errors.state && <span className="text-red-500 text-sm">{errors.state.message as string}</span>}
        </div>
        <div className='my-4'>
          <p className='font-raleway text-black text-[16px] font-medium'>Zipcode</p>
          <input 
            type='text' 
            placeholder='Enter Zipcode' 
            className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
            {...register("zipcode")}
          />
          {errors.zipcode && <span className="text-red-500 text-sm">{errors.zipcode.message as string}</span>}
        </div>
      </div>
      <div className='mt-4'>
        <AddImageBox 
          name="bgPhoto"
          title="Upload Cover Photo" 
          description="Upload clear images that showcase your expertise. Ensure each image is well-lit, high-quality, and at least 800x600 pixels (JPEG or PNG format)." 
          selectedImage={selectedResourceImage}
          setSelectedImage={setSelectedResourceImage}
          handleChange={e => {
            const files = (e.target as HTMLInputElement).files;
            if (files && files.length > 0) {
              setValue("bgPhoto", files[0]);
            }
          }}
        />
        {errors.bgPhoto && <span className="text-red-500 text-sm">{errors.bgPhoto.message as string}</span>}
      </div>
      <div className='flex flex-col w-full flex-start my-1'>
        {
          (isFree) ? (
            <div 
              className={`flex flex-row  hover:bg-[#E9E9E9] text-[16px] font-open-sans rounded-md h-[59px] cursor-pointer`}
              onClick={() => setIsFree(prev=> !prev)}
            >
                <CheckBox 
                  className="mx-2 my-auto text-primary-400" 
                  style={{height: 20, width: 20}} 
                />
                <p className="my-auto">This expert resource is free</p>
            </div>
          ) : (
              <>
                <div className="flex flex-col justify-between">
                  <span className="font-raleway text-black text-[16px] font-medium">
                    Session Cost ($)
                  </span>
                  <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] w-full rounded-lg">
                    <input
                      placeholder="0.00"
                      className="border-0 bg-inherit w-full my-1 "
                      type="number"
                      step={0.10}
                      min={0}
                      {...register("total_amount", { valueAsNumber: true })}
                      onChange={handleTotalAmountChange}
                    />
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                      <LeftArrow className=" text-black rounded-full  m-auto" />
                    </div>
                    <div
                      className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    >
                        <RightArrow className="text-black rounded-full  my-auto" />
                    </div>
                  </label>
                  {errors.total_amount && <span className="text-red-500 text-sm">{errors.total_amount.message as string}</span>}
                </div>
                <div 
                  className={`flex flex-row  text-[16px] font-open-sans rounded-md my-1 h-[59px] cursor-pointer`}
                  onClick={() => setIsFree(prev=> !prev)}
                >
                  <CheckBoxOutlineBlank 
                    className="mx-2 text-[#CFCFCF] my-auto" 
                    style={{height: 20, width: 20}} 
                  />
                  <p className="my-auto">This expert resource is free</p>
                </div>
              </>
          )
        }
      </div>
      {/* <div className='flex flex-row items-center my-4'>
        <input type="checkbox" id="is_verified" {...register("is_verified")}
          className="mr-2 h-5 w-5 border-[#E9E9E9] rounded" />
        <label htmlFor="is_verified" className="font-open-sans text-[16px]">I verify this expert resource is accurate.</label>
      </div> */}
      {/* {errors.is_verified && <span className="text-red-500 text-sm">{errors.is_verified.message as string}</span>} */}
      <label className="my-3 flex items-start gap-2 font-open-sans text-sm text-ink">
        <input
          type="checkbox"
          data-testid="dscvry-listing-checkbox"
          checked={isDscvryListing}
          onChange={(event) => {
            if (event.target.checked) applyDscvryListing();
            else setIsDscvryListing(false);
          }}
          className="mt-1"
        />
        <span>This is the DSCVRY contact list builder expert. Venture setup stays in the DSCVRY Business Hub. HomeCook loads that hub, builds the contact list, then hands the list back. It does not approve a facility.</span>
      </label>
      <PartnerHandoffCreateToggle enabled={usePartnerHandoff} onToggle={setUsePartnerHandoff} />
      <div className='flex flex-row justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button className='border-0 font-raleway text-[#4D4D4D]' type="button">Save to Drafts</button>
        <button 
          className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md'
          type="submit"
        >
            Publish Resource
        </button>
      </div>
    </form>
  ) 
}

export default AddResourceForm